import { AgeRangeOptions, CareFormatOptions } from "@src/constants";
import { useApplicationContext } from "@src/context";
import { useFilterContext } from "@src/context/FilterContext";
import { useProviderListContext } from "@src/context/ProviderListContext";
import ClientLogsService from "@src/services/client-logs.service";
import { buildProviderSearchRequest, getProviderList } from "@src/services/provider-search.service";
import { mapProviderList } from "@src/utils/mapping-utils";
import { isUnder12AgeRange } from "@src/utils/scheduling-metadata";
import { styled } from "@uhg-abyss/web/tools/styled";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import { Fragment } from "react";
import { isPrescriberProviderType } from "../utils/provider-filter-utils";

const FilterComponent = styled("div", {
  flexGrow: 1, // Allow the component to grow and take up available space
  flexBasis: "0", // Ensure all components share equal space
  minWidth: "170px",
  maxWidth: "238px",
  variants: {
    isSmallerTablet: {
      true: {
        minWidth: "150px",
      },
    },
    isMobile: {
      true: {
        maxWidth: "none", // Remove maxWidth when isMobile is true
      },
    },
  },
});

export const PrimaryFilters = ({
  isMobile,
  isMediumMax,
  isSmallerTablet,
  isTablet,
  isWideTabletMax,
}: {
  isMobile: boolean;
  isMediumMax: boolean;
  isSmallerTablet: boolean;
  isTablet: boolean;
  isWideTabletMax: boolean;
}) => {
  const { primaryFilter, secondaryFilter, updatePrimaryFilter, setSecondaryFilter } =
    useFilterContext(); // Access Filter context
  const { data, setData } = useApplicationContext();
  const { fetchProviders, loading } = useProviderListContext();
  const ageRangeOptions = AgeRangeOptions;
  const isPrescriberService = isPrescriberProviderType(secondaryFilter);

  const refreshProviders = ({
    requestData,
    logContext,
    fetchFailureMessage,
    fetchErrorMessage,
  }: {
    requestData: ReturnType<typeof buildProviderSearchRequest>;
    logContext: string;
    fetchFailureMessage: string;
    fetchErrorMessage: string;
  }) => {
    fetchProviders(async () => {
      try {
        const response = await getProviderList(requestData);
        if (response?.error) {
          setData("providerList", []);
          ClientLogsService.logError(
            data.userUuid || "unknown",
            logContext,
            fetchFailureMessage,
            new Error(response.error),
          );
          return [];
        }

        const mappedProviders = mapProviderList(response?.message ?? []);
        setData("providerList", mappedProviders);
        return mappedProviders;
      } catch (error) {
        if ((error as { name?: string })?.name === "AbortError") {
          return [];
        }

        setData("providerList", []);
        ClientLogsService.logError(
          data.userUuid || "unknown",
          logContext,
          fetchErrorMessage,
          error instanceof Error ? error : new Error("Unknown error"),
        );
        return [];
      }
    });
  };

  const handleAgeChange = (selectedValue: string) => {
    setData("ageRange", selectedValue);
    updatePrimaryFilter("ageRange", selectedValue);

    const isUnder12 = isUnder12AgeRange(selectedValue);
    if (isUnder12) {
      setData("isUnder12InPersonProceed", true);
      setData("sessionType", "In-person");
    } else if (data.isUnder12InPersonProceed) {
      // Releasing the U12 lock: reset both the flag and the forced session type so
      // subsequent searches and the secondary-filter drawer use the correct modality.
      setData("isUnder12InPersonProceed", false);
      setData("sessionType", "no preference");
      setSecondaryFilter((prev) => ({ ...prev, sessionType: "no preference" }));
    }

    // `data` is a stale closure — pass the fresh ageRange and the correct sessionType
    // directly so modality is computed correctly inside buildProviderSearchRequest.
    let sessionTypeOverride: { sessionType: string } | Record<string, never> = {};
    if (isUnder12) {
      sessionTypeOverride = { sessionType: "In-person" };
    } else if (data.isUnder12InPersonProceed) {
      sessionTypeOverride = { sessionType: "no preference" };
    }

    const requestedParamData = buildProviderSearchRequest({
      ...data,
      ageRange: selectedValue,
      ...sessionTypeOverride,
    });

    refreshProviders({
      requestData: requestedParamData,
      logContext: "PrimaryFilters.handleAgeChange",
      fetchFailureMessage: "Failed to fetch provider list on age range change",
      fetchErrorMessage: "Error fetching provider list on age range change",
    });
  };

  const handleCareFormatChange = (selectedValue: string) => {
    setData("careFormat", selectedValue);
    updatePrimaryFilter("careFormat", selectedValue);
    // Keep the drawer's secondary filter in sync so the radio group reflects the chip selection
    setSecondaryFilter((prev) => ({ ...prev, careFormat: selectedValue }));

    const requestedParamData = buildProviderSearchRequest(data, {
      therapytypes: selectedValue,
    });

    refreshProviders({
      requestData: requestedParamData,
      logContext: "PrimaryFilters.handleCareFormatChange",
      fetchFailureMessage: "Failed to fetch provider list on care format change",
      fetchErrorMessage: "Error fetching provider list on care format change",
    });
  };

  return (
    <div
      data-testid="test-id-primary-filters"
      style={{
        display: "flex",
        gap: isSmallerTablet ? "10px" : "20px",
        width: "100%",
        justifyContent: "center",
      }}
    >
      <FilterComponent isSmallerTablet={isSmallerTablet} isMobile={isMobile}>
        <TextInput
          label={isMobile ? "" : "Location"}
          data-testid="test-id-location-primary-filter"
          value={primaryFilter?.location}
          isDisabled
          aria-label={`Location selected based on zip code is ${primaryFilter.location}`} // Add descriptive label for screen readers
        />
      </FilterComponent>
      {!isMobile && (
        <Fragment>
          <FilterComponent>
            <TextInput
              label="Type of care"
              data-testid="test-id-type-of-care-primary-filter"
              value={primaryFilter?.typeOfService}
              isDisabled
              aria-label="Type of care" // Add descriptive label for screen readers
            />
          </FilterComponent>
          {!isTablet && (
            <FilterComponent>
              <SelectInput
                label="Age range served"
                data-testid="test-id-age-range-primary-filter"
                value={primaryFilter?.ageRange}
                options={ageRangeOptions.map((ageRange) => {
                  return { value: ageRange, label: ageRange };
                })}
                onChange={(selectedValue: string) => {
                  handleAgeChange(selectedValue);
                }}
                isDisabled={loading}
                aria-label="Age range" // Add descriptive label for screen readers
              />
            </FilterComponent>
          )}
          {!isWideTabletMax && !isPrescriberService && (
            <FilterComponent>
              <SelectInput
                label="Service type"
                data-testid="test-id-care-format-primary-filter"
                value={primaryFilter?.careFormat}
                options={CareFormatOptions.map((opt) => ({
                  value: opt,
                  label: opt,
                }))}
                onChange={(selectedValue: string) => {
                  handleCareFormatChange(selectedValue);
                }}
                isDisabled={loading}
                aria-label="Service type" // Add descriptive label for screen readers
              />
            </FilterComponent>
          )}
        </Fragment>
      )}
      {!isMediumMax && (
        <FilterComponent>
          <TextInput
            label="Insurance"
            data-testid="test-id-insurance-primary-filter"
            value={primaryFilter.insurancePolicy}
            isDisabled
            aria-label="Insurance" // Add descriptive label for screen readers
          />
        </FilterComponent>
      )}
    </div>
  );
};
